import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { CellContext, RowData } from '@tanstack/react-table';
import { IPostCartItems } from '../../../actions/post-cart.action';
import { CREATE_ORDER_COLUMN_INCOTERM, CREATE_ORDER_NO_ENDCUSTOMER_VALUE } from '../../../configuration';
import { ProductEndcustomerTableCellProps } from '../lxs-create-order.data';

type CustomCellContext<TData extends RowData, TValue> = CellContext<TData, TValue> & {
    products: IPostCartItems[];
    setProducts: Dispatch<SetStateAction<IPostCartItems[]>>;
};

const ProductEndcustomerTableCellRef: React.FC<ProductEndcustomerTableCellProps> = ({
    props,
    headerColumnFilters,
    productsData,
    handleEndCustomerChange,
    resources
}) => {
    const customProps = props as CustomCellContext<IPostCartItems, unknown>;
    const currentProductIndex: number = customProps.row.index;
    const columnFilterIncoterm = headerColumnFilters.find((filter: any) => filter.id === CREATE_ORDER_COLUMN_INCOTERM)?.value as string;

    const relevantProducts = productsData?.find(
        (product: { ProductNumber: string; Incoterm1: string }) =>
            product.ProductNumber === customProps.row.original.ProductNumber && product.Incoterm1 === columnFilterIncoterm
    );

    const hasEndCustomers: boolean | undefined = relevantProducts?.EndCustomers.some(
        (customer: { EndCustomerNumber: string }) => customer.EndCustomerNumber !== CREATE_ORDER_NO_ENDCUSTOMER_VALUE
    );

    useEffect(() => {
        if (relevantProducts?.EndCustomers?.length === 1) {
            const newProducts = customProps.products.slice();
            newProducts[Number(currentProductIndex)].EndCustomerName = relevantProducts?.EndCustomers[0]?.EndCustomerDisplayName ?? '';
            newProducts[Number(currentProductIndex)].EndCustomerNumber = relevantProducts?.EndCustomers[0]?.EndCustomerNumber ?? '';
            newProducts[Number(currentProductIndex)].MinimumQty = relevantProducts?.EndCustomers[0]?.MinimumQty ?? '';
            newProducts[Number(currentProductIndex)].MinimumQtyUnit = relevantProducts?.EndCustomers[0]?.MinimumQtyUnit ?? '';
            newProducts[Number(currentProductIndex)].MinimumQtyOnOrderLevel =
                relevantProducts?.EndCustomers[0]?.MinimumQtyOnOrderLevel ?? false;
            customProps.setProducts(newProducts);
        }
    }, []);

    return (
        <div>
            <fieldset className='msc-form__input-group mb-0'>
                <div className='msc-form__input-group-container'>
                    <select
                        required
                        onInvalid={e => {
                            const event = e.target as HTMLSelectElement;
                            event.setCustomValidity(`${resources.createOrderSelectedProductsEndCustomerRequiredMessage}`);
                        }}
                        id={`endCustomer-${currentProductIndex}`}
                        value={customProps.products[Number(currentProductIndex)].EndCustomerNumber ?? ''}
                        onChange={event => {
                            handleEndCustomerChange(customProps, relevantProducts, event, currentProductIndex);
                            const target = event.target as HTMLSelectElement;
                            target.setCustomValidity('');
                        }}
                    >
                        {hasEndCustomers && relevantProducts?.EndCustomers && relevantProducts?.EndCustomers?.length > 1 && (
                            <option value=''>{resources.createOrderSearchDefaultOption}</option>
                        )}
                        {relevantProducts?.EndCustomers.map((endCustomer: any) => {
                            if (endCustomer.EndCustomerNumber === CREATE_ORDER_NO_ENDCUSTOMER_VALUE) {
                                return (
                                    <option value={endCustomer.EndCustomerNumber} key={CREATE_ORDER_NO_ENDCUSTOMER_VALUE}>
                                        {resources.createOrderSelectedProductsTableNoEndCustomer}
                                    </option>
                                );
                            }
                            return (
                                <option value={endCustomer.EndCustomerNumber} key={endCustomer.EndCustomerNumber}>
                                    {endCustomer.EndCustomerDisplayName}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </fieldset>
        </div>
    );
};

export default ProductEndcustomerTableCellRef;
